/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: unified
 */
import React, { useCallback, useEffect, useRef } from 'react';
import {
  ActivityIndicator,
  KeyboardAvoidingView,
  Platform,
  ScrollView,
  StyleSheet,
  Text,
  TextInput,
  TouchableOpacity,
  View,
} from 'react-native';
import { useNavigation } from '@react-navigation/native';
import { Ionicons } from '@expo/vector-icons';
import { ShellContentFrame } from '../../shared/layout/ShellContentFrame';
import { spacing } from '../../shared/theme/spacing';
import { ensureTicketFonts } from '../../shared/ticket/ensureTicketFonts'
import { ticketColors, ticketFonts } from '../../shared/theme/ticketColors';
import { useAiChat } from '../application/useAiChat';
import type { AiChatMessage } from '../domain/aiTypes';

const TOOL_LABELS: Record<string, string> = {
  sales_summary: 'Consultando ventas',
  product_info: 'Buscando producto',
  customer_fiado: 'Consultando cuentas',
  low_stock: 'Revisando stock',
  top_products: 'Top de productos',
  sales_comparison: 'Comparando ventas',
  expiring_products: 'Vencimientos',
  top_debtors: 'Deudores',
  restock_suggestion: 'Sugerencia de compra',
  acknowledge: 'Pensando...',
};

function ChatBubble({ message }: { message: AiChatMessage }) {
  const isUser = message.role === 'user';
  const toolLabel = message.toolName ? (TOOL_LABELS[message.toolName] ?? 'Consultando') : null;
  return (
    <View
      style={[
        styles.bubble,
        isUser ? styles.bubbleUser : styles.bubbleAssistant,
      ]}
    >
      {message.isLoading ? (
        <ActivityIndicator size="small" color={ticketColors.gold} />
      ) : (
        <Text style={[styles.bubbleText, isUser && styles.bubbleTextUser]}>
          {message.content}
        </Text>
      )}
      {toolLabel ? (
        <View style={styles.toolCall}>
          <Ionicons name="sparkles" size={12} color={ticketColors.gold} />
          <Text style={styles.toolCallText}>{toolLabel}</Text>
        </View>
      ) : null}
    </View>
  );
}

export function AiChatScreen() {
  const navigation = useNavigation();
  const { messages, input, setInput, loading, warning, suggested, send, sendSuggested, reset } = useAiChat();
  const scrollRef = useRef<ScrollView>(null);

  useEffect(() => {
    ensureTicketFonts();
  }, []);

  useEffect(() => {
    setTimeout(() => scrollRef.current?.scrollToEnd({ animated: true }), 100);
  }, [messages]);

  const handleSend = useCallback(async () => {
    await send(input);
  }, [input, send]);

  return (
    <ShellContentFrame>
      <KeyboardAvoidingView
        style={styles.container}
        behavior={Platform.OS === 'ios' ? 'padding' : undefined}
        keyboardVerticalOffset={90}
      >
        <View style={styles.header}>
          <TouchableOpacity onPress={() => navigation.goBack()} style={styles.backBtn}>
            <Ionicons name="arrow-back" size={22} color={ticketColors.ink} />
          </TouchableOpacity>
          <Text style={styles.headerTitle}>Asistente IA</Text>
          <TouchableOpacity onPress={reset} style={styles.backBtn}>
            <Ionicons name="refresh" size={20} color={ticketColors.ink3} />
          </TouchableOpacity>
        </View>

        {warning ? (
          <View style={styles.warning}>
            <Text style={styles.warningText}>{warning}</Text>
          </View>
        ) : null}

        <ScrollView
          ref={scrollRef}
          style={styles.scroll}
          contentContainerStyle={styles.scrollContent}
          keyboardShouldPersistTaps="handled"
        >
          {messages.length <= 1 ? (
            <View style={styles.suggestedWrap}>
              <Text style={styles.suggestedTitle}>Algunas preguntas para empezar</Text>
              {suggested.map((prompt) => (
                <TouchableOpacity
                  key={prompt}
                  style={styles.suggestedChip}
                  onPress={() => sendSuggested(prompt)}
                  disabled={loading}
                >
                  <Ionicons name="sparkles" size={14} color={ticketColors.gold} style={styles.suggestedIcon} />
                  <Text style={styles.suggestedText}>{prompt}</Text>
                </TouchableOpacity>
              ))}
            </View>
          ) : null}
          {messages.map((m) => (
            <ChatBubble key={m.id} message={m} />
          ))}
        </ScrollView>

        <View style={styles.inputRow}>
          <TextInput
            style={styles.input}
            value={input}
            onChangeText={setInput}
            placeholder="Escribí tu pregunta..."
            placeholderTextColor={ticketColors.ink3}
            returnKeyType="send"
            onSubmitEditing={handleSend}
            multiline={false}
            editable={!loading}
          />
          <TouchableOpacity
            style={[styles.sendBtn, !input.trim() && styles.sendBtnDisabled]}
            onPress={handleSend}
            disabled={!input.trim() || loading}
          >
            <Ionicons name="send" size={18} color={'#fff'} />
          </TouchableOpacity>
        </View>
      </KeyboardAvoidingView>
    </ShellContentFrame>
  );
}

const styles = StyleSheet.create({
  container: { flex: 1 },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm,
    borderBottomWidth: 1,
    borderBottomColor: ticketColors.rule,
    backgroundColor: ticketColors.paper,
  },
  backBtn: { padding: 6 },
  headerTitle: {
    fontSize: 17,
    fontWeight: '700',
    color: ticketColors.ink,
    fontFamily: ticketFonts.sans,
  },
  warning: {
    backgroundColor: '#fff4e6',
    padding: spacing.sm,
    margin: spacing.md,
    borderRadius: 10,
  },
  warningText: { fontSize: 13, color: ticketColors.ink2, fontFamily: ticketFonts.sans },
  scroll: { flex: 1 },
  scrollContent: { padding: spacing.md, gap: 12 },
  bubble: {
    maxWidth: '80%',
    padding: 12,
    borderRadius: 14,
    marginVertical: 4,
  },
  bubbleUser: {
    alignSelf: 'flex-end',
    backgroundColor: ticketColors.gold,
    borderBottomRightRadius: 4,
  },
  bubbleAssistant: {
    alignSelf: 'flex-start',
    backgroundColor: ticketColors.ruleSoft,
    borderBottomLeftRadius: 4,
  },
  bubbleText: {
    fontSize: 15,
    lineHeight: 20,
    color: ticketColors.ink,
    fontFamily: ticketFonts.sans,
  },
  bubbleTextUser: { color: '#fff' },
  toolCall: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    marginTop: 6,
  },
  toolCallText: {
    fontSize: 12,
    color: ticketColors.gold,
    fontFamily: ticketFonts.sans,
  },
  suggestedWrap: { gap: 8, marginBottom: 12 },
  suggestedTitle: {
    fontSize: 14,
    color: ticketColors.ink2,
    fontFamily: ticketFonts.sans,
    marginBottom: 4,
  },
  suggestedChip: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    padding: 12,
    borderRadius: 10,
    backgroundColor: ticketColors.paper,
    borderWidth: 1,
    borderColor: ticketColors.ruleSoft,
  },
  suggestedIcon: { marginRight: 4 },
  suggestedText: { fontSize: 15, color: ticketColors.ink, fontFamily: ticketFonts.sans },
  inputRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    padding: spacing.md,
    backgroundColor: ticketColors.paper,
    borderTopWidth: 1,
    borderTopColor: ticketColors.rule,
  },
  input: {
    flex: 1,
    borderWidth: 1,
    borderColor: ticketColors.ruleSoft,
    borderRadius: 10,
    padding: 12,
    fontSize: 15,
    color: ticketColors.ink,
    fontFamily: ticketFonts.sans,
    backgroundColor: ticketColors.paper,
    maxHeight: 80,
  },
  sendBtn: {
    width: 44,
    height: 44,
    borderRadius: 10,
    backgroundColor: ticketColors.gold,
    alignItems: 'center',
    justifyContent: 'center',
  },
  sendBtnDisabled: { backgroundColor: ticketColors.ink3 },
});
